import React, { useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/AddDocument.css';
import {
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_OPTGROUPS,
} from '../constants/documentTypes';

const UPDATE_WORKFORCE_DOCUMENT = `
  mutation UpdateWorkforceDocument($input: UpdateWorkforceDocumentInput!) {
    updateWorkforceDocument(input: $input) {
      id
      documentType
      cleaningLicense
      experienceYears
      fileName
      fileUrl
      expiryDate
      siteReference
    }
  }
`;

const REMOVE_WORKFORCE_DOCUMENT = `
  mutation RemoveWorkforceDocument($id: String!) {
    removeWorkforceDocument(id: $id)
  }
`;

const DOC_TYPE_OPTIONS = DOCUMENT_TYPE_OPTIONS;

export default function EditDocument({ group, onClose, onSaved }) {
  const initialFiles = useMemo(
    () =>
      (group?.files || []).map((f) => ({
        id: f.id,
        fileName: f.fileName || f.name || '',
        fileUrl: f.fileUrl || f.url || '',
        documentType: f.documentType || '',
        otherType:
          f.documentType && !DOC_TYPE_OPTIONS.includes(f.documentType)
            ? f.documentType
            : '',
        expiryDate: String(f.expiryDate || '').slice(0, 10),
        siteReference: f.siteReference || '',
      })),
    [group],
  );

  const [cleaningLicense, setCleaningLicense] = useState(
    group?.cleaningLicense || 'No',
  );
  const [experience, setExperience] = useState(
    String(group?.experienceYears ?? '').replace(/[^\d.]/g, '') || '0',
  );
  const [files, setFiles] = useState(initialFiles);
  const [saving, setSaving] = useState(false);

  const setFileField = (id, patch) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    );
  };

  const handleDeleteFile = async (fileId) => {
    if (!await appConfirmMessage('Delete this file?')) return;
    try {
      await graphqlRequest(REMOVE_WORKFORCE_DOCUMENT, { id: fileId });
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success('File deleted.');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete file.');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error('No documents left to update. Add a new document instead.');
      return;
    }

    const experienceYears = Number(experience);
    if (Number.isNaN(experienceYears) || experienceYears < 0) {
      toast.error('Enter a valid experience value.');
      return;
    }

    for (const file of files) {
      const type =
        file.documentType === 'Other'
          ? file.otherType.trim()
          : file.documentType.trim();
      if (!type) {
        toast.error(`Document type is required for ${file.fileName || 'file'}.`);
        return;
      }
    }

    setSaving(true);
    try {
      await Promise.all(
        files.map((file) => {
          const documentType =
            file.documentType === 'Other'
              ? file.otherType.trim()
              : file.documentType.trim();
          return graphqlRequest(UPDATE_WORKFORCE_DOCUMENT, {
            input: {
              id: file.id,
              documentType,
              cleaningLicense,
              experienceYears,
              expiryDate: file.expiryDate || null,
              siteReference: file.siteReference.trim() || null,
            },
          });
        }),
      );
      toast.success('Documents updated.');
      window.dispatchEvent(new Event('workforce-documents-refresh'));
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to update documents.');
    } finally {
      setSaving(false);
    }
  };

  if (!group) return null;

  return (
    <div className="add-document-workforce-overlay">
      <div className="add-document-workforce-container">
        <div className="add-document-workforce-navbar">
          <h3 className="add-document-workforce-title">Edit Document</h3>
          <button
            type="button"
            className="add-document-workforce-close-btn"
            onClick={onClose}
          >
            <FaTimes />
          </button>
        </div>

        <form className="add-document-workforce-form" onSubmit={handleSave}>
          <label className="add-document-workforce-label">Employee</label>
          <input
            className="add-document-workforce-input"
            value={`${group.employeeId || ''} — ${group.name || ''}`}
            disabled
          />

          <label className="add-document-workforce-label">
            Cleaning License <span className="required-star">*</span>
          </label>
          <select
            className="add-document-workforce-select"
            value={cleaningLicense}
            onChange={(e) => setCleaningLicense(e.target.value)}
            required
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          <label className="add-document-workforce-label">
            Experience (Years) <span className="required-star">*</span>
          </label>
          <input
            type="number"
            min="0"
            max="50"
            className="add-document-workforce-input"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            required
          />

          <label className="add-document-workforce-label">Files</label>
          {files.length === 0 ? (
            <p className="add-document-hint">No files left for this employee.</p>
          ) : (
            <div className="edit-document-files">
              {files.map((file) => {
                const selectValue = DOC_TYPE_OPTIONS.includes(file.documentType)
                  ? file.documentType
                  : file.documentType
                    ? 'Other'
                    : '';
                return (
                  <div key={file.id} className="edit-document-file-row">
                    <div className="edit-document-file-top">
                      {file.fileUrl ? (
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="file-link"
                        >
                          {file.fileName || 'View file'}
                        </a>
                      ) : (
                        <span>{file.fileName || 'File'}</span>
                      )}
                      <button
                        type="button"
                        className="edit-document-file-delete"
                        title="Delete file"
                        onClick={() => void handleDeleteFile(file.id)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                    <select
                      className="add-document-workforce-select"
                      value={selectValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFileField(file.id, {
                          documentType: value,
                          otherType:
                            value === 'Other' ? file.otherType || '' : '',
                        });
                      }}
                      required
                    >
                      <option value="">Select document type</option>
                      {DOCUMENT_TYPE_OPTGROUPS.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {selectValue === 'Other' && (
                      <input
                        type="text"
                        className="add-document-workforce-input"
                        placeholder="Enter document type"
                        value={file.otherType}
                        onChange={(e) =>
                          setFileField(file.id, { otherType: e.target.value })
                        }
                        required
                      />
                    )}
                    <input
                      type="date"
                      className="add-document-workforce-input"
                      value={file.expiryDate}
                      onChange={(e) =>
                        setFileField(file.id, { expiryDate: e.target.value })
                      }
                      aria-label={`Expiry date for ${file.fileName || 'document'}`}
                    />
                    <input
                      type="text"
                      className="add-document-workforce-input"
                      placeholder="Site / project reference (optional)"
                      value={file.siteReference}
                      maxLength="255"
                      onChange={(e) =>
                        setFileField(file.id, { siteReference: e.target.value })
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className="add-document-workforce-btn-row">
            <button
              type="submit"
              className="add-document-workforce-upload-btn"
              disabled={saving || files.length === 0}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
